import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'next-i18next';

import type { types as bossClientTypes } from '@boss/boss-client';
import { useRouter } from '@boss/hooks';

const campaignKeys = {
  participatingProducts: (locale: string, campaignId: string, categories: string[]) => [
    'participatingProducts',
    locale,
    campaignId,
    categories,
  ],
};

/**
 * Participating products for one campaign + categories (ticket 45171).
 * Hits /api/campaign/products (server-side proxy to the client-provided
 * /v2/campaign/products endpoint). Response order = display order.
 * `categories` is optional — empty, the API returns all products for the campaign.
 */
const useParticipatingProducts = (campaignId: string, categories: string[], enabled = true) => {
  const { t } = useTranslation('common');
  const { locale } = useRouter();

  return useQuery({
    queryKey: campaignKeys.participatingProducts(locale, campaignId, categories),
    queryFn: async () => {
      const params = new URLSearchParams();

      params.set('campaignId', campaignId);
      categories.forEach(category => params.append('category', category));
      params.set('locale', locale);

      const response = await fetch(`/api/campaign/products?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`campaign products fetch failed: ${response.status}`);
      }

      return (await response.json()) as bossClientTypes.campaign.ICampaignProduct[];
    },
    enabled: enabled && !!campaignId,
    meta: {
      errorMessage: t('toast.participatingProducts.error.title'),
    },
  });
};

export { useParticipatingProducts };
